/**
 * ApiKeysManager — Gestor de API Keys reusable.
 *
 * Antes vivía como un tab dentro de SettingsPage; ahora es un componente
 * autónomo (self-contained) que puede embeberse en cualquier hub.
 * Los endpoints `/api/api-keys` (GET, POST, DELETE) NO cambiaron.
 */
import { useState, useEffect } from 'react';
import { APP_NAME } from '@/lib/brand';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Key, Plus, Copy, Trash2, ShieldCheck, AlertTriangle, BookOpen } from 'lucide-react';

export default function ApiKeysManager() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState('');
  const [creating, setCreating] = useState(false);

  const load = () => {
    api.get('/api-keys')
      .then(r => setApiKeys(Array.isArray(r.data) ? r.data : []))
      .catch(() => setApiKeys([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Ponle un nombre a la clave (ej. "Integración Zapier")');
      return;
    }
    setCreating(true);
    try {
      const r = await api.post('/api-keys', { name: newKeyName.trim() });
      setCreatedKey(r.data.key);
      setNewKeyName('');
      toast.success('API Key creada — cópiala ahora, no se mostrará completa otra vez');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creando clave');
    } finally {
      setCreating(false);
    }
  };

  const deleteApiKey = async (id) => {
    if (!window.confirm('¿Eliminar esta clave? Las integraciones que la usen dejarán de funcionar.')) return;
    try {
      await api.delete(`/api-keys/${id}`);
      toast.success('API Key eliminada');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiado al portapapeles');
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Copiado al portapapeles');
      } catch {
        toast.info('No se pudo copiar automáticamente. Selecciona la clave y cópiala manualmente.');
      }
    }
  };

  return (
    <div className="space-y-5" data-testid="api-keys-manager">
      {/* Explicación amigable */}
      <Card className="bg-violet-500/5 border-violet-500/20" data-testid="api-keys-help-card">
        <CardContent className="p-4 flex gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center shrink-0">
            <BookOpen className="w-5 h-5 text-violet-300" />
          </div>
          <div className="space-y-1">
            <p className="text-white font-semibold text-sm">¿Qué es una API Key?</p>
            <p className="text-white/70 text-xs leading-relaxed">
              Es una "contraseña secreta" que le entregas a otros sistemas (Zapier, n8n, tu propia
              web, scripts, etc.) para que puedan leer/escribir leads, propiedades y oportunidades
              en tu cuenta de {APP_NAME}, <span className="text-violet-300 font-semibold">sin compartir tu usuario y contraseña</span>.
              Genera una clave distinta por cada integración para poder revocarla si algo se filtra.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Form crear */}
      <Card className="bg-card/50 backdrop-blur-md border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base text-white flex items-center gap-2">
            <Key className="w-4 h-4 text-violet-400" /> Tus API Keys
          </CardTitle>
          <CardDescription className="text-xs text-white/60">
            Cada clave se asocia a tu empresa. Úsala con el header <code className="text-violet-300">X-Api-Key</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {createdKey && (
            <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/30" data-testid="new-key-banner">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold">
                  Nueva clave generada — guárdala AHORA
                </p>
              </div>
              <div className="flex items-center gap-2">
                <code className="text-xs text-white font-mono break-all flex-1 bg-black/30 p-2 rounded" data-testid="new-key-value">{createdKey}</code>
                <Button variant="outline" size="sm" onClick={() => copy(createdKey)} className="border-emerald-400/30 text-emerald-300 shrink-0" data-testid="copy-new-key-btn">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                </Button>
              </div>
              <div className="mt-2 flex items-start gap-2 text-[10px] text-amber-300">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                <span>Después de cerrar esta vista solo verás los primeros 8 caracteres. Cópiala y pégala en tu integración.</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setCreatedKey('')} className="mt-2 h-7 text-[10px] text-white/60" data-testid="hide-new-key-btn">
                Ya la copié, ocultar
              </Button>
            </div>
          )}

          <div className="flex gap-2">
            <Input
              data-testid="api-key-name"
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              placeholder='Ej. "Zapier - Producción" o "Mi sitio web"'
              className="bg-black/20 border-white/10 focus:border-primary"
              onKeyDown={e => e.key === 'Enter' && createApiKey()}
            />
            <Button
              onClick={createApiKey}
              disabled={creating || !newKeyName.trim()}
              className="bg-violet-500 hover:bg-violet-500/90 text-white font-bold shrink-0"
              data-testid="create-api-key-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Crear
            </Button>
          </div>

          {loading ? (
            <div className="py-6 text-center text-xs text-white/40">Cargando…</div>
          ) : apiKeys.length === 0 ? (
            <div className="py-8 text-center" data-testid="no-keys-empty">
              <Key className="w-10 h-10 mx-auto text-white/15 mb-2" />
              <p className="text-xs text-white/50">Aún no tienes claves. Crea la primera arriba.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {apiKeys.map(k => (
                <div key={k.id} className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5" data-testid={`api-key-row-${k.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-white font-medium truncate">{k.name}</p>
                    <p className="text-[10px] text-white/40 font-mono mt-0.5">
                      {k.key} {k.last_used ? <span className="ml-2 text-cyan-300">· último uso: {String(k.last_used).split('T')[0]}</span> : <span className="ml-2 text-white/30">· nunca usada</span>}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => deleteApiKey(k.id)} className="h-7 w-7 p-0 text-white/40 hover:text-rose-400 shrink-0" data-testid={`delete-key-${k.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Endpoints v1 — referencia rápida */}
      <Card className="bg-card/40 border-white/5" data-testid="api-v1-endpoints-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-sm text-white">Endpoints REST v1 disponibles</CardTitle>
          <CardDescription className="text-xs text-white/60">
            Header obligatorio: <code className="text-violet-300">X-Api-Key: tu_clave_aqui</code>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[11px] font-mono">
            {[
              ['GET', '/api/v1/leads', 'emerald'],
              ['POST', '/api/v1/leads', 'blue'],
              ['GET', '/api/v1/properties', 'emerald'],
              ['POST', '/api/v1/properties', 'blue'],
              ['GET', '/api/v1/opportunities', 'emerald'],
              ['POST', '/api/v1/opportunities', 'blue'],
              ['GET', '/api/v1/pipeline', 'emerald'],
              ['GET', '/api/v1/team', 'emerald'],
            ].map(([m, p, color]) => (
              <div key={`${m}${p}`} className="flex items-center gap-2 p-2 rounded bg-black/20 border border-white/5">
                <Badge variant="outline" className={`border-${color}-400/30 text-${color}-300 text-[9px] uppercase w-12 justify-center`}>{m}</Badge>
                <code className="text-[10px] text-white/70 truncate">{p}</code>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-white/40 mt-3">
            ¿Necesitas más endpoints? Crea una entidad personalizada en el tab <span className="text-cyan-300">Generador de APIs</span>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
